// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Img, { ImgProps, StyledImgWrapper } from "@store/MobiusOverrides/Img/Img";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const enum fields {
    imageUrl = "imageUrl",
    altText = "altText",
    text = "text",
    textPosition = "textPosition",
    marginTop = "marginTop",
    marginBottom = "marginBottom",
    textWidth = "textWidth",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.imageUrl]: string;
        [fields.altText]: string;
        [fields.text]: string;
        [fields.textPosition]: "left" | "right";
        [fields.marginTop]: number;
        [fields.marginBottom]: number;
        [fields.textWidth]: GridWidths;
    };
}

export interface LetterLayoutTextAndImageStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    gridItemImg?: GridItemProps;
    textWrapper?: TypographyPresentationProps;
    seperator?: InjectableCss;
    img?: ImgProps;
}

export const letterLayoutTextAndImageStyles: LetterLayoutTextAndImageStyles = {
    container: {
        gap: 0,
        css: css`
            position: relative;
        `,
    },
    gridItem: {
        css: css`
            z-index: 1;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
        `,
    },
    textWrapper: {
        css: css`
            ${breakpointValues([`${pxToRem(24)}`, `${pxToRem(36)}`, null], ["padding"])}
        `,
    },
    seperator: {
        css: css`
            height: 0;
            border-bottom: 3px dotted ${getColor("accent")};
            width: 100%;
            position: relative;

            &.dotLeft:after,
            &.dotRight:after {
                content: "";
                display: block;
                width: 11px;
                height: 11px;
                background-color: ${getColor("accent")};
                position: absolute;
                top: -4px;
                border-radius: 50%;
            }
            &.dotLeft {
                ${breakpoint("sm", "down")} {
                    margin-left: 30px;
                    width: calc(100% - 30px);
                }
                &:after {
                    left: 0;
                }
            }
            &.dotRight {
                ${breakpoint("sm", "down")} {
                    margin-right: 30px;
                    width: calc(100% - 30px);
                }
                &:after {
                    right: 0;
                }
            }
        `,
    },
    gridItemImg: {
        width: [12, 12, 6, 6, 6],
        css: css`
            z-index: 0;
            ${breakpointValues(
                [
                    `${pxToRem(24)} ${pxToRem(24)} 0 ${pxToRem(24)}`,
                    `${pxToRem(36)} ${pxToRem(24)} 0 ${pxToRem(24)}`,
                    null,
                ],
                ["padding"],
            )}
            justify-content: center;
            ${StyledImgWrapper} {
                justify-content: center;
            }
        `,
    },
    img: {
        css: css`
            background-color: transparent;
            display: block;
            width: auto;
            height: auto;
            ${breakpointValues(["90%", "90%", "auto"], ["width"])}
            ${breakpointValues(["360px", "420px", "100%"], ["max-height"])}
            ${breakpointValues(["400px", "600px", "100%"], ["max-width"])}
        `,
    },
};

const styles = letterLayoutTextAndImageStyles;

type Props = OwnProps;

const LetterLayoutTextAndImage = ({ fields }: Props) => {
    const gridItem = {
        ...styles.gridItem,
        width: [12, 12, fields.textWidth, fields.textWidth, fields.textWidth] as GridWidths[],
    };
    const gridItemImg = {
        ...styles.gridItemImg,
        width: [12, 12, 12 - fields.textWidth, 12 - fields.textWidth, 12 - fields.textWidth] as GridWidths[],
        css: css`
            ${styles.gridItemImg?.css}
            ${breakpointValues(
                [null, null, pxToRem(56), pxToRem(64), pxToRem(72)],
                [fields.textPosition === "left" ? "padding-right" : "padding-left"],
            )}
            ${StyledImgWrapper} {
                ${breakpointValues(
                    [
                        null,
                        null,
                        pxToRem(fields.marginTop * 0.5),
                        pxToRem(fields.marginTop * 0.75),
                        pxToRem(fields.marginTop),
                    ],
                    ["margin-top"],
                )}
                ${breakpointValues(
                    [
                        null,
                        null,
                        pxToRem(fields.marginBottom * 0.5),
                        pxToRem(fields.marginBottom * 0.75),
                        pxToRem(fields.marginBottom),
                    ],
                    ["margin-bottom"],
                )}
            }
        `,
    };
    const textWrapper = {
        ...styles.textWrapper,
        css: css`
            ${styles.textWrapper?.css}
            ${breakpointValues([null, null, fields.textPosition === "left" ? "left" : "right"], ["text-align"])}
            ${breakpointValues([null, null, pxToRem(56), pxToRem(64), pxToRem(72)], ["padding-left", "padding-right"])}
        `,
    };
    const imgNode = (
        <GridItem {...gridItemImg}>
            <Img className="img" src={fields.imageUrl} altText={fields.altText} loading="lazy" {...styles.img} />
        </GridItem>
    );

    const textNode = (
        <GridItem {...gridItem}>
            <Typography as="div" {...textWrapper}>
                {parse(fields.text, parserOptions)}
            </Typography>
            <StyledWrapper
                as="div"
                className={fields.textPosition === "left" ? "dotRight" : "dotLeft"}
                aria-hidden={true}
                {...styles.seperator}
            />
        </GridItem>
    );

    return (
        <StyledWrapper {...styles.wrapper}>
            <Hidden above="sm">
                <GridContainer {...styles.container}>
                    {imgNode}
                    {textNode}
                </GridContainer>
            </Hidden>
            <Hidden below="md">
                <GridContainer {...styles.container}>
                    {fields.textPosition === "left" ? textNode : imgNode}
                    {fields.textPosition === "left" ? imgNode : textNode}
                </GridContainer>
            </Hidden>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: LetterLayoutTextAndImage,
    definition: {
        group: "Basic",
        allowedContexts: ["WelcomeLetterPage"],
        displayName: "Welcome Letter Content",
        fieldDefinitions: [
            {
                name: fields.imageUrl,
                fieldType: "Contextual",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                name: fields.altText,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.text,
                fieldType: "Contextual",
                editorTemplate: "RichTextField",
                defaultValue: "",
            },
            {
                name: fields.textPosition,
                displayName: "Position of text content",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                defaultValue: "left",
                fieldType: "Contextual",
            },
            {
                name: fields.marginTop,
                displayName: "Top Margin",
                tooltip: "Use a negative value to have your image overlap under the previous content section.",
                editorTemplate: "FullIntegerField",
                defaultValue: 0,
                fieldType: "Contextual",
            },
            {
                name: fields.marginBottom,
                displayName: "Bottom Margin",
                tooltip: "Use a negative value to have your image overlap under the next content section.",
                editorTemplate: "FullIntegerField",
                defaultValue: 0,
                fieldType: "Contextual",
            },
            {
                name: fields.textWidth,
                displayName: "Text width",
                tooltip: "Width of text section at maximum resolution. Total screen width is 12.",
                editorTemplate: "IntegerField",
                defaultValue: 6,
                min: 3,
                max: 9,
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
